import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocale } from '../i18n/LocaleContext'
import { useCamps } from '../hooks/useCamps'
import { useFAQs } from '../hooks/useFAQs'

const SCRIPT_ID = 'mha-structured-data'

// Localized breadcrumb labels keyed by the canonical (hu) route slug.
// Each entry also lists the ro slug so we can recognise the page in either language.
const BREADCRUMBS: Record<string, { ro: string; label: { hu: string; ro: string } }> = {
  rolunk: { ro: 'despre', label: { hu: 'Rólunk', ro: 'Despre noi' } },
  kapcsolat: { ro: 'contact', label: { hu: 'Kapcsolat', ro: 'Contact' } },
  feltetelek: { ro: 'termeni', label: { hu: 'Felhasználási feltételek', ro: 'Termeni și Condiții' } },
  adatvedelem: {
    ro: 'confidentialitate',
    label: { hu: 'Adatvédelmi irányelvek', ro: 'Politica de Confidențialitate' },
  },
  'cookie-szabalyzat': {
    ro: 'politica-cookies',
    label: { hu: 'Cookie-szabályzat', ro: 'Politica de cookie-uri' },
  },
  beiratkozas: { ro: 'inscriere', label: { hu: 'Beiratkozás', ro: 'Înscriere' } },
}

/**
 * Injects JSON-LD structured data (schema.org) into <head> so search engines and
 * social platforms can surface the organisation, the website, individual camps and
 * the FAQ as rich results. Rebuilds whenever locale or CMS content changes, and is
 * resilient: when camps/FAQs are empty it still emits the core Organization /
 * LocalBusiness / WebSite graph.
 */
export default function StructuredData() {
  const { locale } = useLocale()
  const { camps } = useCamps()
  const { faqs } = useFAQs()
  const { pathname } = useLocation()

  useEffect(() => {
    const origin = window.location.origin

    const organization = {
      '@type': 'Organization',
      '@id': `${origin}#organization`,
      name: 'Magic Harghita Adventures',
      url: origin,
      telephone: '+40 753 116 373',
      email: 'info@magicadventures.ro',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Str. Principală Nr. 511',
        addressLocality: 'Căpâlnița',
        addressRegion: 'Harghita',
        addressCountry: 'RO',
      },
    }

    const graph: Record<string, unknown>[] = [
      organization,
      {
        '@type': 'LocalBusiness',
        '@id': `${origin}#localbusiness`,
        name: 'Magic Harghita Adventures',
        url: origin,
        telephone: '+40 753 116 373',
        email: 'info@magicadventures.ro',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Str. Principală Nr. 511',
          addressLocality: 'Căpâlnița',
          addressRegion: 'Harghita',
          addressCountry: 'RO',
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${origin}#website`,
        name: 'Magic Harghita Adventures',
        url: origin,
        inLanguage: locale,
        publisher: { '@id': `${origin}#organization` },
      },
    ]

    // One Course per active camp.
    for (const camp of camps) {
      if (camp.active === false) continue
      const name = camp.title?.[locale]
      if (!name) continue
      graph.push({
        '@type': 'Course',
        name,
        description: camp.shortDescription?.[locale] ?? '',
        inLanguage: locale,
        provider: {
          '@type': 'Organization',
          name: 'Magic Harghita Adventures',
          '@id': `${origin}#organization`,
        },
      })
    }

    // FAQPage from CMS FAQs.
    const faqEntries = faqs
      .filter((f) => f.question?.[locale] && f.answer?.[locale])
      .map((f) => ({
        '@type': 'Question',
        name: f.question[locale],
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer[locale],
        },
      }))
    if (faqEntries.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: faqEntries,
      })
    }

    // BreadcrumbList for non-home routes (Home -> current page).
    const parts = pathname.split('/').filter(Boolean)
    const slug = parts[1] // after the /hu or /ro locale prefix
    if (slug) {
      // Resolve the canonical key whether the URL uses the hu or ro slug.
      const key =
        slug in BREADCRUMBS
          ? slug
          : Object.keys(BREADCRUMBS).find((k) => BREADCRUMBS[k].ro === slug)
      const crumb = key ? BREADCRUMBS[key] : undefined
      if (crumb) {
        graph.push({
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: locale === 'hu' ? 'Kezdőlap' : 'Acasă',
              item: `${origin}/${locale}`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: crumb.label[locale],
              item: `${origin}${pathname}`,
            },
          ],
        })
      }
    }

    const json = safeStringify({ '@context': 'https://schema.org', '@graph': graph })
    if (!json) return

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = SCRIPT_ID
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = json

    return () => {
      document.getElementById(SCRIPT_ID)?.remove()
    }
  }, [locale, camps, faqs, pathname])

  return null
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}
